/**
 * The chef Delivery page's route (`/api/kitchen-orders/delivery-route`):
 * per delivery day, the stops in driving order, when to leave, arrivals,
 * instructions and the Google Maps links, all computed on the server
 * (`DeliveryRouteManager`), plus the kitchen's saved settings (stop order,
 * minutes at each stop).
 *
 * What to unload at each stop is NOT here: the page composes it from the
 * shipments read (`useKitchenShipmentsQuery` + `shipmentsToCampuses`), so the
 * route says exactly what the Orders page says.
 *
 * Times are "HH:mm" on the kitchen's local clock.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from './http'
import type { TempLogGateDay } from './kitchen-orders'

export type DeliveryRoutePlanStatus = 'OK' | 'NEEDS_DRIVE_TIMES' | 'NO_TIME_WINDOWS' | 'NO_STOPS'

export type DeliveryRouteDriveStatus =
  | 'OK'
  | 'NOT_CONFIGURED'
  | 'UNAVAILABLE'
  | 'TOO_MANY_POINTS'
  | 'NO_KITCHEN_ADDRESS'
  | 'NOT_NEEDED'

export interface DeliveryRouteAddress {
  line1: string
  city: string
  stateProvince: string
  postalCode: string
  /** "196 Cesar E Chavez, Pontiac, MI 48342". */
  text: string
}

export interface DeliveryRouteStop {
  schoolLocationId: string
  name: string
  organizationName: string | null
  address: DeliveryRouteAddress | null
  deliveryWindowText: string | null
  window: { start: string | null; end: string | null } | null
  windowNote: string | null
  mealStarts: Array<{ mealtime: string; time: string }>
  contactName: string | null
  contactPhone: string | null
  instructions: string | null
  dryRun: 'DRY_RUN' | 'TASTING' | null
  received: { count: number; of: number; lastAt: string | null }
  shipments: Array<{
    id: string
    mealtime: string
    serviceDate: string
    totalServings: number
    receivedAt: string | null
  }>
  items: Array<{ mealtime: string; serviceDate: string; name: string; servings: number }>
  driveMinutes: number | null
  driveMeters: number | null
  deadline: string | null
  arrival: string | null
  unloadAt: string | null
  departure: string | null
  waitMinutes: number
  lateMinutes: number
  directionsUrl: string | null
}

export interface DeliveryRouteDay {
  /** `YYYY-MM-DD` delivery date. */
  date: string
  order: { source: 'SAVED' | 'SUGGESTED'; basis: 'DRIVE_TIMES' | 'WINDOWS' }
  plan: {
    status: DeliveryRoutePlanStatus
    leaveBy: string | null
    bindingStopId: string | null
    finishAt: string | null
    lateStopCount: number
    totalDriveMinutes: number | null
  }
  stops: DeliveryRouteStop[]
  notDriven: DeliveryRouteStop[]
  missingAddressStopIds: string[]
  googleMapsUrls: string[]
  /**
   * The day's route page, opened with no sign-in (`/route/<token>`, relative: put the app's origin in front), and
   * the last day it opens. Null when the day drives no stop or the server cannot sign links. Optional only for
   * fixtures that predate it.
   */
  link?: { path: string; expiresOn: string } | null
}

export interface DeliveryRouteSettings {
  stopMinutes: number
  stopOrder: string[] | null
  updatedAt: string | null
}

export interface DeliveryRouteResponse {
  kitchen: { id: string; name: string; timeZone: string; address: DeliveryRouteAddress | null }
  settings: DeliveryRouteSettings
  driveTimes: { status: DeliveryRouteDriveStatus; source: 'GOOGLE_ROUTES'; traffic: false }
  days: DeliveryRouteDay[]
  /** Overdue temperature logs: the route is withheld (days is empty), as on Orders. */
  tempLogGate: { overdueDays: TempLogGateDay[] } | null
}

export interface DeliveryRouteVariables {
  kitchenLocationId: string
  /** `YYYY-MM-DD`, inclusive. */
  startDate: string
  endDate: string
}

export const deliveryRouteQueryKey = (vars: DeliveryRouteVariables) =>
  ['kitchen-orders', 'delivery-route', vars] as const

export async function fetchDeliveryRoute(
  vars: DeliveryRouteVariables,
  signal?: AbortSignal,
): Promise<DeliveryRouteResponse> {
  return apiFetch<DeliveryRouteResponse>('/api/kitchen-orders/delivery-route', {
    query: { kitchenLocationId: vars.kitchenLocationId, startDate: vars.startDate, endDate: vars.endDate },
    signal,
  })
}

export function useDeliveryRouteQuery(
  vars: DeliveryRouteVariables,
  opts: { enabled?: boolean; keepPrevious?: boolean } = {},
) {
  return useQuery<DeliveryRouteResponse>({
    queryKey: deliveryRouteQueryKey(vars),
    queryFn: ({ signal }) => fetchDeliveryRoute(vars, signal),
    enabled: (opts.enabled ?? true) && Boolean(vars.kitchenLocationId),
    // Addresses, windows and drive times change rarely (and are cached on the
    // server), but "Received" lands during the drive: refresh each minute.
    staleTime: 60_000,
    refetchInterval: 60_000,
    ...(opts.keepPrevious ? { placeholderData: keepPreviousData } : {}),
  })
}

export interface UpdateDeliveryRouteSettingsVariables {
  kitchenLocationId: string
  /** One day's stops in the new order (merged into the standing order), or null for the suggestion. */
  stopOrder?: string[] | null
  stopMinutes?: number
}

export function useUpdateDeliveryRouteSettingsMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateDeliveryRouteSettingsVariables) =>
      apiFetch<{ settings: DeliveryRouteSettings }>('/api/kitchen-orders/delivery-route/settings', {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: () => {
      // Every range's plan uses the standing order and the stop time.
      void qc.invalidateQueries({ queryKey: ['kitchen-orders', 'delivery-route'] })
    },
  })
}
